import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Empty, Button } from 'antd';
import cn from 'classnames';
import { memo, useCallback, useMemo, useRef } from 'react';

import { buildLeadSortableId, buildStageDroppableId } from '../../../../utils';
import { LeadsFunnelLeadCard } from '../../LeadsFunnelLeadCard';
import { LeadsFunnelLeadCardSkeleton } from '../../LeadsFunnelLeadCardSkeleton';
import { LeadsFunnelSortableLeadCard } from '../../LeadsFunnelSortableLeadCard';
import { useLeadsFunnelStageVirtualCards } from '../hooks/useLeadsFunnelStageVirtualCards';

import type { LeadsFunnelBoardStage } from '../../../../model/types';
import type { CSSProperties, FC, UIEvent } from 'react';

import './LeadsFunnelStageColumn.scss';

// Fixed-height virtualization constants. Kept in sync with the CSS variables set
// on the scroll container below (and consumed by the card / spacer styles).
const LEADS_FUNNEL_CARD_HEIGHT = 112;
const LEADS_FUNNEL_CARD_GAP = 6;
const LEADS_FUNNEL_CARD_OVERSCAN = 6;
const LEADS_FUNNEL_LOAD_MORE_THRESHOLD = 240;
const LOAD_MORE_SKELETON_COUNT = 3;

const listStyle: CSSProperties = {
  '--leads-funnel-card-height': `${LEADS_FUNNEL_CARD_HEIGHT}px`,
  '--leads-funnel-card-gap': `${LEADS_FUNNEL_CARD_GAP}px`,
} as CSSProperties;

interface LeadsFunnelStageColumnProps {
  stage: LeadsFunnelBoardStage;
  canShowContacts: boolean;
  isDragEnabled: boolean;
  isDragActive: boolean;
  hasMoreLeads: boolean;
  isLoadingMore: boolean;
  onLoadMore(): void;
}

const renderLoadingCards = () =>
  Array.from({ length: LOAD_MORE_SKELETON_COUNT }, (_, index) => (
    <LeadsFunnelLeadCardSkeleton key={`loading-lead-${index}`} />
  ));

const LeadsFunnelStageColumnBase: FC<LeadsFunnelStageColumnProps> = ({
  canShowContacts,
  hasMoreLeads,
  isDragEnabled,
  isDragActive,
  onLoadMore,
  stage,
  isLoadingMore,
}) => {
  const dndStageId = stage.id;
  const isStageDroppable = isDragEnabled && dndStageId !== null;

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: buildStageDroppableId(dndStageId ?? 0),
    disabled: !isStageDroppable,
    data: { stageId: dndStageId },
  });

  const hasMoreStageLeads = stage.leads.length < stage.totalLeads;
  const lastLoadMoreLeadsCountRef = useRef<number | null>(null);

  const {
    scrollContainerRef,
    visibleLeads,
    topSpacerHeight,
    bottomSpacerHeight,
    hasScrollableContent,
    updateScrollTop,
  } = useLeadsFunnelStageVirtualCards({
    leads: stage.leads,
    isDragActive,
    cardHeight: LEADS_FUNNEL_CARD_HEIGHT,
    cardGap: LEADS_FUNNEL_CARD_GAP,
    overscan: LEADS_FUNNEL_CARD_OVERSCAN,
  });

  const shouldShowLoadMoreButton =
    hasMoreLeads &&
    hasMoreStageLeads &&
    !hasScrollableContent &&
    !isLoadingMore;

  const loadMoreHandler = useCallback(() => {
    if (isLoadingMore || !hasMoreLeads || !hasMoreStageLeads) return;

    onLoadMore();
  }, [hasMoreLeads, hasMoreStageLeads, isLoadingMore, onLoadMore]);

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      updateScrollTop(scrollTop);

      if (isLoadingMore || !hasMoreLeads || !hasMoreStageLeads) return;

      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      // Guard against re-triggering before `isLoadingMore` flips: only one
      // load-more request per appended batch (keyed on the current count).
      if (
        distanceToBottom <= LEADS_FUNNEL_LOAD_MORE_THRESHOLD &&
        lastLoadMoreLeadsCountRef.current !== stage.leads.length
      ) {
        lastLoadMoreLeadsCountRef.current = stage.leads.length;
        onLoadMore();
      }
    },
    [
      hasMoreLeads,
      hasMoreStageLeads,
      isLoadingMore,
      onLoadMore,
      stage.leads.length,
      updateScrollTop,
    ]
  );

  const sortableIds = useMemo(
    () => visibleLeads.map(({ lead }) => buildLeadSortableId(lead.Id)),
    [visibleLeads]
  );

  return (
    <section
      ref={setDroppableRef}
      className={cn('LeadsFunnelStageColumn', {
        'LeadsFunnelStageColumn--drop-active': isStageDroppable && isOver,
      })}
    >
      <div className="LeadsFunnelStageColumn__header">
        <span
          className="LeadsFunnelStageColumn__color"
          style={{ backgroundColor: stage.color ?? '#d9d9d9' }}
          aria-hidden="true"
        />
        <div className="LeadsFunnelStageColumn__title-block">
          <span className="LeadsFunnelStageColumn__title">{stage.name}</span>
          <span className="LeadsFunnelStageColumn__funnel">
            {stage.description || stage.funnelName}
          </span>
        </div>
        <span className="LeadsFunnelStageColumn__count">
          {stage.leads.length} / {stage.totalLeads}
        </span>
      </div>

      <div
        className="LeadsFunnelStageColumn__list"
        ref={scrollContainerRef}
        onScroll={scrollHandler}
        style={listStyle}
      >
        {stage.leads.length === 0 ? (
          <div className="LeadsFunnelStageColumn__empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Лидов нет"
            />
          </div>
        ) : (
          <>
            {topSpacerHeight > 0 && (
              <div
                className="LeadsFunnelStageColumn__spacer"
                style={{ height: topSpacerHeight }}
                aria-hidden="true"
              />
            )}

            {isStageDroppable && dndStageId !== null ? (
              <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
              >
                {visibleLeads.map(({ lead }) => (
                  <LeadsFunnelSortableLeadCard
                    key={lead.Id}
                    lead={lead}
                    canShowContacts={canShowContacts}
                    funnelId={stage.funnelId}
                    stageId={dndStageId}
                  />
                ))}
              </SortableContext>
            ) : (
              visibleLeads.map(({ lead }) => (
                <LeadsFunnelLeadCard
                  key={lead.Id}
                  lead={lead}
                  canShowContacts={canShowContacts}
                  funnelId={stage.funnelId}
                  stageId={stage.id}
                />
              ))
            )}

            {bottomSpacerHeight > 0 && (
              <div
                className="LeadsFunnelStageColumn__spacer"
                style={{ height: bottomSpacerHeight }}
                aria-hidden="true"
              />
            )}
          </>
        )}

        {isLoadingMore && hasMoreStageLeads && renderLoadingCards()}

        {shouldShowLoadMoreButton && (
          <Button
            className="LeadsFunnelStageColumn__show-next"
            block
            size="small"
            onClick={loadMoreHandler}
          >
            Показать следующие
          </Button>
        )}
      </div>
    </section>
  );
};

export const LeadsFunnelStageColumn = memo(LeadsFunnelStageColumnBase);
